/**
 * CDP SDK — the Monfri consent banner and the CMP bridges (СТ22, launch 07.10).
 *
 * A site created after 07.10 runs with `consent: { mode: 'anonymous-until-consent',
 * banner: true }`: until the visitor decides, the SDK counts anonymously from
 * memory (nothing written to the device — see the persistence gate in
 * storage.ts), and this banner asks. When the site has its own consent manager,
 * the decision comes from there instead and this banner never shows:
 *
 *  - IAB TCF v2.2 (`__tcfapi`) — bridged in consent-mode.ts;
 *  - Cookiebot, CookieYes, Complianz, OneTrust — bridged here;
 *  - any other known manager (Usercentrics, Iubenda, Klaro, CookieScript,
 *    Termly, Osano, cookieconsent) — detected, not bridged: the banner stays
 *    away and the site passes the decision with cdp.consent().
 *
 * "Accept" and "Decline" carry the same weight on screen — the EDPB's
 * guidance is that refusing must be as easy as accepting.
 */

import type { ConsentPreferences } from './types';
import { log, warn } from './utils';

const ALL: ConsentPreferences = { functional: true, analytics: true, marketing: true, personalization: true };
const NONE: ConsentPreferences = { functional: true, analytics: false, marketing: false, personalization: false };

type Win = Window & Record<string, any>;

// =============================================================================
// CMP DETECTION + BRIDGES
// =============================================================================

/** True when the page carries a consent manager of its own. */
export function detectCmp(): string | null {
  const w = window as Win;
  try {
    if (typeof w.__tcfapi === 'function') return 'tcf';
    if (w.Cookiebot || w.CookieConsent?.consent?.stamp !== undefined) return 'cookiebot';
    if (typeof w.getCkyConsent === 'function') return 'cookieyes';
    if (typeof w.cmplz_has_consent === 'function' || document.getElementById('cmplz-cookiebanner-container')) return 'complianz';
    if (w.OneTrust || w.OptanonActiveGroups !== undefined || w.OnetrustActiveGroups !== undefined) return 'onetrust';
    if (w.UC_UI || w.usercentrics) return 'usercentrics';
    if (w._iub) return 'iubenda';
    if (w.klaro) return 'klaro';
    if (w.CookieScript) return 'cookiescript';
    if (w.Termly) return 'termly';
    if (w.Osano) return 'osano';
    if (w.cookieconsent?.initialise || w.CookieConsentApi) return 'cookieconsent';
  } catch {
    /* a hostile global getter — treat as no CMP */
  }
  return null;
}

function fromCookiebot(): ConsentPreferences | null {
  const cb = (window as Win).Cookiebot;
  if (!cb || !cb.consent || !cb.hasResponse) return null;
  return {
    functional: true,
    analytics: !!cb.consent.statistics,
    marketing: !!cb.consent.marketing,
    personalization: !!cb.consent.preferences,
  };
}

function fromCookieYes(): ConsentPreferences | null {
  const get = (window as Win).getCkyConsent;
  if (typeof get !== 'function') return null;
  const c = get();
  if (!c || !c.isUserActionCompleted || !c.categories) return null;
  return {
    functional: true,
    analytics: !!c.categories.analytics,
    marketing: !!c.categories.advertisement,
    personalization: !!c.categories.functional,
  };
}

function fromComplianz(): ConsentPreferences | null {
  const has = (window as Win).cmplz_has_consent;
  if (typeof has !== 'function') return null;
  // Complianz answers before a choice too (its own defaults); wait for the visitor's.
  const status = document.cookie.match(/(^| )cmplz_banner-status=([^;]+)/);
  if (!status || decodeURIComponent(status[2]) !== 'dismissed') return null;
  return {
    functional: true,
    analytics: !!has('statistics'),
    marketing: !!has('marketing'),
    personalization: !!has('preferences'),
  };
}

function fromOneTrust(): ConsentPreferences | null {
  const w = window as Win;
  const groups: string | undefined = w.OnetrustActiveGroups ?? w.OptanonActiveGroups;
  if (typeof groups !== 'string') return null;
  // A visitor who has not decided has no OptanonAlertBoxClosed cookie.
  if (!/(^| )OptanonAlertBoxClosed=/.test(document.cookie)) return null;
  const has = (g: string) => groups.includes(`,${g},`);
  return { functional: true, analytics: has('C0002'), marketing: has('C0004'), personalization: has('C0003') };
}

/**
 * Forward a non-TCF consent manager's decision to the SDK, now and whenever the
 * visitor changes it. Returns a disposer. TCF is bridged separately
 * (consent-mode.ts initTcfBridge).
 */
export function initCmpBridges(onChange: (consent: ConsentPreferences) => void): () => void {
  const readers: Array<() => ConsentPreferences | null> = [fromCookiebot, fromCookieYes, fromComplianz, fromOneTrust];
  const apply = () => {
    for (const read of readers) {
      try {
        const c = read();
        if (c) {
          log('Consent: CMP decision received');
          onChange(c);
          return;
        }
      } catch {
        /* one manager failing must not stop the others */
      }
    }
  };
  const events: Array<[EventTarget, string]> = [
    [window, 'CookiebotOnConsentReady'],
    [window, 'CookiebotOnAccept'],
    [window, 'CookiebotOnDecline'],
    [document, 'cookieyes_consent_update'],
    [document, 'cmplz_status_change'],
    [document, 'cmplz_fire_categories'],
    [window, 'OneTrustGroupsUpdated'],
  ];
  for (const [target, name] of events) target.addEventListener(name, apply);
  apply();
  return () => {
    for (const [target, name] of events) target.removeEventListener(name, apply);
  };
}

// =============================================================================
// THE BANNER
// =============================================================================

const TEXT = {
  bg: {
    body: 'Използваме бисквитки и подобни технологии, за да измерваме посещенията, да подобряваме сайта и за маркетинг. Можете да приемете или да откажете — сайтът работи и в двата случая.',
    more: 'Повече информация',
    accept: 'Приемам',
    decline: 'Отказвам',
    label: 'Съгласие за бисквитки',
  },
  en: {
    body: 'We use cookies and similar technologies to measure visits, improve this site and for marketing. You can accept or decline — the site works either way.',
    more: 'More information',
    accept: 'Accept',
    decline: 'Decline',
    label: 'Cookie consent',
  },
};

const BANNER_ID = 'monfri-consent-banner';

export interface BannerOptions {
  /** 'bg' or anything else (English). */
  language?: string;
  /** The site's privacy or cookie page. */
  policyUrl?: string;
  onDecision: (consent: ConsentPreferences) => void;
}

function pickLanguage(language?: string): 'bg' | 'en' {
  const lang = (language || document.documentElement.lang || navigator.language || 'en').toLowerCase();
  return lang.startsWith('bg') ? 'bg' : 'en';
}

/** Show the banner (once per page). Safe to call before the body exists. */
export function showConsentBanner(options: BannerOptions): void {
  if (typeof document === 'undefined') return;
  if (!document.body) {
    document.addEventListener('DOMContentLoaded', () => showConsentBanner(options), { once: true });
    return;
  }
  if (document.getElementById(BANNER_ID)) return;

  const t = TEXT[pickLanguage(options.language)];
  const host = document.createElement('div');
  host.id = BANNER_ID;
  // The page's CSS cannot reach into the shadow root, and ours cannot leak out.
  const root = host.attachShadow ? host.attachShadow({ mode: 'open' }) : (host as unknown as ShadowRoot);

  const policy = options.policyUrl && /^https?:\/\//i.test(options.policyUrl)
    ? `<a class="more" href="${options.policyUrl.replace(/"/g, '&quot;')}" target="_blank" rel="noopener">${t.more}</a>`
    : '';
  // LA-08 (monfri docs/legal/audits/LEGAL-FULL-AUDIT-2026-10-20.md): the banner has to
  // lead to the site's privacy policy. No default address is invented here — it is
  // the site's own page — but its absence is said where the site's developer looks;
  // the CDP dashboard warns the owner when the banner is on without one.
  if (!policy) {
    warn('Consent banner shown without a link to the site\'s privacy policy — set it in the CDP dashboard (Settings → Tracking → Consent).');
  }

  root.innerHTML = `
    <style>
      :host { all: initial; }
      .card { position: fixed; z-index: 2147483646; left: 16px; right: 16px; bottom: 16px; max-width: 440px;
        box-sizing: border-box; padding: 16px; border-radius: 12px; background: #ffffff; color: #111827;
        border: 1px solid #e5e7eb; box-shadow: 0 10px 30px rgba(0,0,0,.15);
        font: 14px/1.5 system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; }
      p { margin: 0 0 12px; }
      .more { color: #2563eb; text-decoration: underline; }
      .row { display: flex; gap: 8px; }
      button { flex: 1; min-height: 40px; padding: 8px 12px; border-radius: 8px; border: 1px solid #111827;
        background: #111827; color: #ffffff; font: 600 14px/1.2 inherit; cursor: pointer; }
      button:focus-visible { outline: 3px solid #93c5fd; outline-offset: 2px; }
      @media (prefers-color-scheme: dark) {
        .card { background: #111827; color: #f9fafb; border-color: #374151; }
        button { background: #f9fafb; color: #111827; border-color: #f9fafb; }
        .more { color: #93c5fd; }
      }
    </style>
    <div class="card" role="dialog" aria-live="polite" aria-label="${t.label}">
      <p>${t.body} ${policy}</p>
      <div class="row">
        <button type="button" data-choice="decline">${t.decline}</button>
        <button type="button" data-choice="accept">${t.accept}</button>
      </div>
    </div>`;

  root.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null;
    const choice = target?.getAttribute?.('data-choice');
    if (!choice) return;
    host.remove();
    options.onDecision(choice === 'accept' ? { ...ALL } : { ...NONE });
  });

  document.body.appendChild(host);
  log('Consent banner shown');
}

export function hideConsentBanner(): void {
  document.getElementById(BANNER_ID)?.remove();
}
